import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';

export interface InboxLinkResult {
  email: string;
  provider: string;
  label: string;
  url: string;
}

/**
 * The email address a "sniper link" filters the user's inbox down to.
 *
 * Different flows send from different addresses, so callers pass the sender that
 * matches the email they told the user to look for. `email` is the full "from"
 * address; `domain` is used by providers whose search filter only accepts a
 * domain (Yahoo/AOL).
 */
export interface InboxSender {
  email: string;
  domain: string;
}

/**
 * Signup verification emails, configured on the "signup_verification"
 * transactional message in Customer.io.
 * Subject: "Action required to verify your Customer.io account".
 */
export const SIGNUP_VERIFICATION_SENDER: InboxSender = {
  email: 'win@customer.io',
  domain: 'customer.io',
};

/**
 * Build a sniper link URL for the given provider, user email, and sender.
 *
 * Sniper links deep-link into the user's inbox with search parameters that
 * surface only the relevant email, cutting through spam folders and
 * multi-account confusion. See https://growth.design/sniper-link
 */
const SNIPER_URL_BUILDERS: Record<string, (encodedEmail: string, sender: InboxSender) => string> = {
  google: (_encodedEmail, sender) =>
    `https://mail.google.com/mail/u/0/#search/from%3A(${encodeURIComponent(sender.email)})+in%3Aanywhere+newer_than%3A1d`,
  microsoft: (encodedEmail) => `https://outlook.live.com/mail/?login_hint=${encodedEmail}`,
  microsoft365: (encodedEmail) => `https://outlook.office365.com/mail/?login_hint=${encodedEmail}`,
  yahoo: (_encodedEmail, sender) => `https://mail.yahoo.com/d/search/keyword=from%253A${sender.domain}`,
  aol: (_encodedEmail, sender) => `https://mail.aol.com/d/search/keyword=from%253A${sender.domain}`,
  protonmail: (_encodedEmail, sender) => `https://mail.proton.me/u/0/all-mail#from=${sender.email}`,
  apple: () => 'https://www.icloud.com/mail',
  zoho: () => 'https://mail.zoho.com',
  fastmail: (_encodedEmail, sender) =>
    `https://app.fastmail.com/mail/search:from%3A${encodeURIComponent(sender.email)}/`,
  yandex: () => 'https://mail.yandex.com',
  godaddy: () => 'https://email.godaddy.com',
  rackspace: () => 'https://apps.rackspace.com',
};

function buildSniperUrl(provider: string, email: string, sender: InboxSender): string {
  const builder = SNIPER_URL_BUILDERS[provider];
  if (builder == null) {
    return '';
  }
  return builder(encodeURIComponent(email), sender);
}

/**
 * Resolve the given email address to a provider-specific "sniper link" inbox URL.
 *
 * Delegates provider detection to the `/v1/inbox_link` services endpoint, which performs
 * an MX lookup. This handles consumer mailboxes (gmail.com, outlook.com, ...) and custom
 * domains hosted on Google Workspace / Microsoft 365 / etc. — the latter is the common
 * case for business signups, which a static domain-to-provider map would miss.
 *
 * `sender` controls which address the sniper link filters the inbox down to; it
 * defaults to the signup verification sender for backward compatibility.
 */
export async function getInboxLinkAsync(
  email: string,
  sender: InboxSender = SIGNUP_VERIFICATION_SENDER,
): Promise<InboxLinkResult | null> {
  if (isEmptyString(email)) {
    return null;
  }

  try {
    const data = await hydraUiApi.mxLookup.inboxLink({ body: { email } });

    if (data.found !== true || data.provider == null || data.label == null || data.email == null) {
      return null;
    }

    // The backend returns 'yahoo' for AOL domains since they share MX infrastructure.
    // Distinguish AOL by domain so we build the correct sniper link URL and label.
    const isAol = data.email.split('@')[1]?.toLowerCase() === 'aol.com';
    const provider = isAol ? 'aol' : data.provider;
    const label = isAol ? 'AOL Mail' : data.label;
    const url = buildSniperUrl(provider, data.email, sender);
    if (isEmptyString(url)) {
      return null;
    }

    return {
      email: data.email,
      provider,
      label,
      url,
    };
  } catch {
    return null;
  }
}
